import { ReactNode } from "react"

import { Discount } from "./discount"
import { Competence } from "./competences"

import { ExperienceCategory } from "@modules/PsyCatalog/common/ExperienceCategory"

export interface Contact {
    public_number: string
    ext: string
    city: string
}

export interface OfferedService {
    service_type_id: number
    name: string
    description: string
    online: boolean
    family: boolean
    price: number
    price_grade: string | null
    price_grade_name?: string
    public: boolean
    service_type_group: OfferedServiceGroup
    available: boolean
}

export interface OfferedServiceGroup {
    id: number
    name: string
    public_name_for_clients: string
    family: boolean
    priority: number
}

export interface PsychologistMethod {
    id: number
    code: string
    name: string
    short_name: string
    description: ReactNode
    slug: string
}

export interface PsychologistCity {
    name: string
    slug: string
}

export type WorkingWithSingle = Record<
    "adults" | "believer" | "family" | "fem" | "friendly" | "foreigner" | "elderly" | "couples",
    boolean
>

export type WorkingWithCollections = Record<"children", string[]>

export type WorkingWithAdults = Record<
    "adults" | "believer" | "fem" | "friendly" | "foreigner" | "elderly" | "couples" | "cancer",
    boolean
>

export type WorkingWithChildren = Record<
    "children_3_10" | "children_11_15" | "children_16_18",
    boolean
>

export type WorkingWithFamily = Record<"family", boolean>

export interface WorkingWithCategories {
    adults: WorkingWithAdults
    children: WorkingWithChildren
    family: WorkingWithFamily
}

export enum SlotType {
    Online = "online",
    InPerson = "in_person",
    Free = "free",
    Any = "any",
}

export type AvailableForBooking = Record<SlotType, string | null>

export enum Sex {
    Male = "M",
    Female = "F",
    Any = "any",
}

export enum MARITAL_STATUS {
    Unknown = "",
    Married = "M",
    Single = "S",
    Partnership = "P",
    Remarried = "R",
    Divorced = "D",
    Widow = "W",
}

type SexOfPsychologist = Sex.Male | Sex.Female
type MaritalStatusOfPsychologist =
    | MARITAL_STATUS.Unknown
    | MARITAL_STATUS.Married
    | MARITAL_STATUS.Single
    | MARITAL_STATUS.Partnership
    | MARITAL_STATUS.Remarried
    | MARITAL_STATUS.Divorced
    | MARITAL_STATUS.Widow

export interface Psychologist {
    discount?: Discount
    display_name: string
    first_name: string
    last_name: string // Фамилия
    middle_name: string // Отчество
    hash_id: string
    city_details: string
    new_city: PsychologistCity
    contact: Contact
    sex: SexOfPsychologist
    age: number | null
    experience: number
    private_experience: number | null
    alter_experience: number | null
    alter_practice_start: Date | null
    marital_status: MaritalStatusOfPsychologist
    has_children: boolean
    face_url: string
    offered_services: OfferedService[]
    online_booking: boolean
    promote: boolean
    education: string[]
    higher_education: string[]
    practical_education: string[]
    about: string
    titles: string[]
    competences: Competence[]
    methods: PsychologistMethod[]
    working_with: WorkingWithCategories
    available_for_booking: AvailableForBooking
    features_of_work: string[]
    expertise: string[]
    audio: string | null
    experience_category: ExperienceCategory | null
    booking_count: number
    profile_url: string
    video_call_handle: string
    allow_time_request: boolean
    experiment_group: boolean
    evidence_based: boolean
    video?: string
    have_media?: boolean
    active_community_member: string
    slots?: PsyNearestSlots
}

enum BUSINESS_LEGAL_STATUS {
    UNKNOWN = "",
    INDIVIDUAL_ENTREPRENEUR = "E",
    SELF_EMPLOYED = "S",
}

export interface PsychologistAddressDetails extends Psychologist {
    addresses: string
    video_call_handle: string
}

export interface PsychologistDetailed extends Psychologist {
    addresses: string
    video_call_handle: string
    business_legal_status: BUSINESS_LEGAL_STATUS
    collect_clients_feedback: boolean
    b2b_offered_services: OfferedService[]
    email: string
    google_email: string
    voximplant_user_name: string
    telegram_id: boolean
    telegram_username: string | null
    private_number: string | null
}

export interface PsyAccountSettings {
    calendar_id?: string | null
    min_hours_before_online_session?: number
    min_hours_before_offline_session?: number
    max_days_before_session?: number
    has_psy_online_service?: boolean
    has_psy_offline_service?: boolean
}

export interface LastPsychologist {
    hash_id: string
    last_booking_service_type_id: number
    allow_fast_booking: boolean
    allow_time_request_if_no_slots: boolean
    has_paid_session: boolean
}

interface PsyNearestSlots {
    count: number
    nearest: string
}
